import React, { useEffect } from 'react';
import './App.css';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Dashboard from './pages/dashboard/Dashboard';
import Login from './pages/login/Login';
import Opportunity from './pages/opportunity/Opportunity';
import OpportunityDetail from './pages/opportunityDetail/OpportunityDetail';
import OpportunitiesByRegion from './pages/opportunityByRegion/OpportunitiesByRegion';
import ViewPitch from './pages/viewPitch/ViewPitch';
import SharedOpportunity from './pages/sharedOpportunity/SharedOpportunity';
// import ExportedOpportunities from './pages/exportedOpportunities/ExportedOpportunities';
import StoresMapPage from './pages/storesMap/StoresMapPage';
import UserList from './pages/userList/UserList';
import YourLists from './pages/yourLists/YourLists';
import ListStores from './pages/listStores/ListStores';
import AllFiles from './pages/allFiles/AllFiles';
import PenetrationReport from './pages/penetrationReport/PenetrationReport';
import PenetrationReportV2 from './pages/penetrationReportV2/PenetrationReportV2';
import CustomerHealth from './pages/customerHealth/CustomerHealth';
import CustomerHealthPortfolio from './pages/customerHealth/Portfolio';
import BrandHealthDetail from './pages/customerHealth/BrandHealthDetail';
import DataMappings from './pages/dataMappings/DataMappings';
import { initializeAuthToken } from './services/graphqlClient';
import { useAuthStore } from './stores';
import { initializeFirebaseAnalytics, trackPageView } from './services/firebaseAnalytics';
import { initializeClarity } from './services/microsoftClarity';
import { initializeGA4, initializePostHog, trackPageView as trackGA4PageView } from './services/analyticsService';
import { preloadBrandLogoCSV } from './utils/brandLogoMapper';
import { Toaster } from 'react-hot-toast';
import { useVersionCheck } from './hooks/useVersionCheck';
import { useDesktopUpdates } from './hooks/useDesktopUpdates';
import { isTauri } from './utils/tauri';
import UpdateBanner from './components/UpdateBanner/UpdateBanner';
import DesktopUpdateBanner from './components/DesktopUpdateBanner/DesktopUpdateBanner';
import TitleBar from './components/TitleBar/TitleBar';
import { openLink } from './utils/openLink';
import { environment } from './environment/environment';

function AppContent(): React.ReactElement {
  const token = useAuthStore((state) => state.token);
  const location = useLocation();
  const updateAvailable = useVersionCheck();
  const { rustUpdate, mcpUpdate, mcpPendingRestart } = useDesktopUpdates();
  const hasDesktopUpdate = !!(rustUpdate || mcpUpdate);
  // const [appVersion, setAppVersion] = useState<string | null>(null);

  // // Surfaces the Tauri app version in the test badge below (disabled).
  // useEffect(() => {
  //   if (isTauri()) {
  //     import('@tauri-apps/api/app').then(({ getVersion }) => getVersion().then(setAppVersion));
  //   }
  // }, []);

  useEffect(() => {
    trackPageView(location.pathname);
    trackGA4PageView(location.pathname);
  }, [location.pathname]);

  return (
    <div className="App">
      {/* Desktop-only custom window title bar (frameless window). Renders null on web. */}
      <TitleBar />
      <UpdateBanner visible={updateAvailable && !hasDesktopUpdate} />
      <DesktopUpdateBanner rustUpdate={rustUpdate} mcpUpdate={mcpUpdate} mcpPendingRestart={mcpPendingRestart} />
      <Routes>
        <Route path="/" element={<Navigate to="/login" />} />

        <Route path="/login" element={token ? <Navigate to="/dashboard" /> : <Login />} />

        <Route path="/dashboard" element={token ? <Dashboard /> : <Navigate to="/login" />}>
          <Route index element={<Navigate to="/dashboard/opportunity" />} />
          <Route path="opportunity" element={<Opportunity />} />
          <Route path="opportunitybymap" element={<OpportunitiesByRegion />} />
          {/* <Route path="exported-opportunities" element={<ExportedOpportunities />} /> */}
          {(environment.environment === 'development' || environment.environment === 'local') && (
            <Route path="customer-health" element={<CustomerHealth />}>
              <Route index element={<CustomerHealthPortfolio />} />
              <Route path=":parentId" element={<BrandHealthDetail />} />
            </Route>
          )}
          <Route path="opportunitydetails/:brandname" element={<OpportunityDetail />} />
          <Route path="viewpitch/:opportunityId" element={<ViewPitch />} />
          <Route path="your-lists" element={<YourLists />} />
          <Route path="list-stores/:listId" element={<ListStores />} />
          <Route path="user-list" element={<UserList />} />
          <Route path="files" element={<AllFiles />} />
          {(environment.environment === 'development' || environment.environment === 'local') && (
            <Route path="penetration-report" element={<PenetrationReport />} />
          )}
          {(environment.environment === 'development' || environment.environment === 'local') && (
            <Route path="penetration-report-v2" element={<PenetrationReportV2 />} />
          )}
        </Route>

        {(environment.environment === 'development' || environment.environment === 'local') && (
          <Route path="/dashboard/data-mappings" element={token ? <DataMappings /> : <Navigate to="/login" />} />
        )}

        <Route path="/shared/:opportunityId" element={<SharedOpportunity />} />

        <Route path="/storemap/:storeid" element={token ? <StoresMapPage /> : <Navigate to="/login" />} />
      </Routes>
    </div>
  );
}

function App(): React.ReactElement {
  useEffect(() => {
    initializeAuthToken();
    initializeFirebaseAnalytics();
    initializeClarity();
    initializeGA4();
    initializePostHog();
    preloadBrandLogoCSV();
  }, []);

  // Desktop: the window is created hidden (tauri.conf.json `visible: false`) so
  // the user never sees a blank white flash while the UI loads. Reveal it only
  // after React has painted its first frame (double rAF guarantees a paint).
  useEffect(() => {
    if (!isTauri()) return;
    let cancelled = false;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (cancelled) return;
      import('@tauri-apps/api/window')
        .then(({ getCurrentWindow }) => getCurrentWindow().show())
        .catch(() => { /* not in Tauri / API unavailable — ignore */ });
    }));
    return () => { cancelled = true; };
  }, []);

  // Desktop: suppress web-browser behaviors so it feels like a native app.
  //  - no right-click "web page" context menu
  //  - dropping a file onto the window never navigates/opens it
  //  - any "open in new tab" link (target="_blank") opens a NEW Atlas window
  //    instead — a desktop webview has no tabs, so these otherwise do nothing.
  //    This one delegated listener covers every current/future new-tab link,
  //    including the ones injected into Google Maps info-window popups.
  // Gated to Tauri, so the web build is unaffected (its drag-drop upload zones
  // and normal new-tab links keep working). The app's own dropzones still
  // receive files: their React onDrop handlers run before this window guard.
  useEffect(() => {
    if (!isTauri()) return;
    const prevent = (e: Event) => e.preventDefault();
    const onAnchorClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0) return;
      const target = e.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest('a[target="_blank"]') as HTMLAnchorElement | null;
      if (anchor && anchor.href) {
        e.preventDefault();
        openLink(anchor.href);
      }
    };
    window.addEventListener('contextmenu', prevent);
    window.addEventListener('dragover', prevent);
    window.addEventListener('drop', prevent);
    window.addEventListener('click', onAnchorClick);
    return () => {
      window.removeEventListener('contextmenu', prevent);
      window.removeEventListener('dragover', prevent);
      window.removeEventListener('drop', prevent);
      window.removeEventListener('click', onAnchorClick);
    };
  }, []);

  return (
    <Router>
      <AppContent />
      <Toaster
        position="top-right"
        toastOptions={{ duration: 3000 }}
        containerStyle={isTauri() ? { top: 44 } : undefined}
      />
    </Router>
  );
}

export default App;
